import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabase'
import Swal from 'sweetalert2'
import TablaCelulares from '../components/inventario/TablaCelulares'
import ModalCelular from '../components/inventario/ModalCelular'

const API_BASE_URL = process.env.REACT_APP_API_URL

const emptyCelularForm = { 
  modelo: '', capacidad: '', color: '', precioInput: '', moneda: 'USD',   
  bateria: '', estado_estetico: 'Usado', estado_venta: 'disponible', accesorios: [] 
}

const formatearTelefonoArgentino = (numeroManual) => {
  let limpio = numeroManual.replace(/\D/g, '')
  if (limpio.startsWith('549')) return limpio 
  if (limpio.startsWith('54') && !limpio.startsWith('549')) return '549' + limpio.substring(2)
  if (limpio.startsWith('0')) limpio = limpio.substring(1) 
  return '549' + limpio 
}

export default function SeccionCelulares({ toast, comercioId }) {
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState(null)
  const [celulares, setCelulares] = useState([])
  const [plantillas, setPlantillas] = useState([]) 
  const [formCelular, setFormCelular] = useState(emptyCelularForm)
  const [statsCelulares, setStatsCelulares] = useState({ total: 0, disponible: 0, pendiente: 0, vendido: 0 })

  // Carga los celulares del inventario
  const cargarCelulares = useCallback(async () => {
    if (!comercioId) return
    const { data, error } = await supabase
      .from('inventario_celulares')
      .select('*')
      .eq('comercio_id', comercioId)
      .order('id', { ascending: false })
    
    if (!error && data) {
      setCelulares(data)
      setStatsCelulares({
        total: data.length,
        disponible: data.filter(x => x.estado_venta === 'disponible').length,
        pendiente: data.filter(x => x.estado_venta === 'pendiente').length,
        vendido: data.filter(x => x.estado_venta === 'vendido').length,
      })
    }
  }, [comercioId])

  // 🌟 Nueva función para sincronizar tus estrategias reales
  const cargarPlantillas = useCallback(async () => {
    if (!comercioId) return
    try {
      const res = await fetch(`${API_BASE_URL}/api/plantillas/${comercioId}`)
      if (res.ok) {
        const dataP = await res.json()
        setPlantillas(dataP)
      }
    } catch (e) {
      console.error("Error cargando plantillas en inventario:", e)
    }
  }, [comercioId])

  useEffect(() => { 
    cargarCelulares() 
    cargarPlantillas() // Sincroniza al montar el componente
  }, [cargarCelulares, cargarPlantillas])

  const abrirModal = (item = null) => {
    if (item) {
      let monedaGuardada = item.precio_ars > 0 ? 'ARS' : 'USD'
      let precioGuardado = item.precio_ars > 0 ? item.precio_ars : item.precio
      setFormCelular({
        ...item,
        bateria: item.bateria ? String(item.bateria).replace('%', '') : '',
        accesorios: item.accesorios ? item.accesorios.split(', ') : [],
        precioInput: Number(precioGuardado).toLocaleString('es-AR'),
        moneda: monedaGuardada,
        estado_estetico: item.estado_estetico || 'Usado'
      })
      setEditId(item.id)
    } else {
      setFormCelular(emptyCelularForm)
      setEditId(null)
    }
    setModal(true)
  }

  const guardar = async (e) => {
    e.preventDefault()
    if (!comercioId) return toast('Error de autenticación', 'error')

    const numeroLimpio = String(formCelular.precioInput).replace(/\./g, '')
    const valorPrecio = numeroLimpio ? parseFloat(numeroLimpio) : 0
    if (valorPrecio <= 0) return toast('Ingresa un precio válido', 'error')

    const payload = { 
      modelo: formCelular.modelo, capacidad: formCelular.capacidad, color: formCelular.color,
      precio: formCelular.moneda === 'USD' ? valorPrecio : 0,       
      precio_ars: formCelular.moneda === 'ARS' ? valorPrecio : 0,   
      bateria: formCelular.bateria ? `${formCelular.bateria}%` : '',
      estado_estetico: formCelular.estado_estetico, estado_venta: formCelular.estado_venta,
      accesorios: formCelular.accesorios.length > 0 ? formCelular.accesorios.join(', ') : '',
      comercio_id: comercioId,
      stock: formCelular.estado_venta === 'disponible' ? 1 : 0
    }

    const { error } = editId
      ? await supabase.from('inventario_celulares').update(payload).eq('id', editId)
      : await supabase.from('inventario_celulares').insert([payload])

    if (error) return toast('Error al guardar celular', 'error')
    cargarCelulares()
    toast('Guardado correctamente', 'success')
    setModal(false)
  }

  const eliminar = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esto!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    })

    if (!result.isConfirmed) return

    const { error } = await supabase.from('inventario_celulares').delete().eq('id', id)
    if (error) {
      toast('Error al eliminar', 'error')
    } else {
      toast('Eliminado correctamente', 'success')
      cargarCelulares()
    }
  }

  // --- REGISTRO DE VENTA DIRECTA REFACTORIZADO ---
  const registrarVentaDirecta = async (celular) => {
    const opcionesPlantillasHtml = plantillas.length > 0
      ? plantillas.map(p => `<option value="${p.id}">${p.nombre} (A los ${p.dias_espera} días)</option>`).join('')
      : `
        <option value="satisfaccion">Control de Satisfacción (A los 3 días)</option>
        <option value="upselling">Venta de Accesorios / Descuentos (A los 7 días)</option>
        <option value="resena">Fidelización y Reseña de Google (A los 15 días)</option>
      `

    const { value: formValues } = await Swal.fire({
      title: 'Registrar Venta de Mostrador',
      width: '460px',
      html: `
        <div style="text-align: left; padding: 0 5px;">
          <div style="background: #f8fafc; border: 1px solid #e2e8f0; padding: 12px 16px; border-radius: 8px; margin-bottom: 16px;">
            <span style="font-size: 11px; color: #64748b; text-transform: uppercase;">Equipo seleccionado</span>
            <div style="font-size: 16px; font-weight: 700; color: #0f172a; margin-top: 4px;">${celular.modelo}</div>
          </div>
          <div style="margin-bottom: 14px;">
            <label style="display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 4px;">Nombre del Cliente</label>
            <input id="swal-input-nombre" placeholder="Ej: Juan Pérez" style="width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px; box-sizing: border-box;">
          </div>
          <div style="margin-bottom: 14px;">
            <label style="display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 4px;">WhatsApp</label>
            <div style="display: flex;">
              <span style="background: #f1f5f9; border: 1px solid #cbd5e1; border-right: none; padding: 0 12px; border-radius: 8px 0 0 8px; display: flex; align-items: center; font-size: 14px; font-weight: 600; color: #64748b;">+54 9</span>
              <input id="swal-input-telefono" type="tel" placeholder="Cód. área + número" style="flex: 1; border: 1px solid #cbd5e1; border-radius: 0 8px 8px 0; padding: 10px 12px; box-sizing: border-box;">
            </div>
          </div>
          <div>
            <label style="display: block; font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 4px;">Estrategia Post-Venta (WhatsApp)</label>
            <select id="swal-input-estrategia" style="width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 10px 12px; background: #fff; font-size: 14px; color: #334155; box-sizing: border-box;">
              ${opcionesPlantillasHtml}
            </select>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Confirmar Venta',
      confirmButtonColor: 'var(--primary, #2563eb)',
      preConfirm: () => {
        const nombre = document.getElementById('swal-input-nombre').value
        const telefono = document.getElementById('swal-input-telefono').value
        const estrategia = document.getElementById('swal-input-estrategia').value
        if (!nombre || !telefono) return Swal.showValidationMessage('Completa ambos campos.')
        if (telefono.replace(/\D/g, '').length < 9) return Swal.showValidationMessage('El número es muy corto.')
        return { nombre, telefono, estrategia }
      }
    })

    if (!formValues) return
    const nombreCliente = formValues.nombre
    const telefonoNacional = formatearTelefonoArgentino(formValues.telefono)
    const estrategiaElegida = formValues.estrategia

    try {
      toast('Procesando venta...', 'info')

      const respuesta = await fetch(`${API_BASE_URL}/api/ventas/directa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comercio_id: comercioId,
          cliente_nombre: nombreCliente,
          telefono: telefonoNacional,
          celulares_ids: [celular.id],
          estrategia: estrategiaElegida 
        })
      })

      if (!respuesta.ok) {
        throw new Error('Error en la respuesta del servidor back-end')
      }

      Swal.fire({ 
        title: '¡Venta Registrada!', 
        text: 'El equipo se descontó y se agendó en el CRM mediante el servidor.', 
        icon: 'success' 
      })
      
      cargarCelulares() 
    } catch (error) {
      console.error(error)
      toast('Error al procesar la venta o registrar en CRM', 'error')
    }
  }

  const celularesFiltrados = celulares.filter(x => 
    x.modelo?.toLowerCase().includes(search.toLowerCase()) || x.color?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="animate-fade-in">
      <div className="inv-stats-grid">
        <div className="inv-stat-card total"><span className="inv-stat-label">Total Equipos</span><span className="inv-stat-value">{statsCelulares.total}</span></div>
        <div className="inv-stat-card disponible"><span className="inv-stat-label">Disponibles</span><span className="inv-stat-value">{statsCelulares.disponible}</span></div>
        <div className="inv-stat-card pendiente"><span className="inv-stat-label">Pendientes</span><span className="inv-stat-value">{statsCelulares.pendiente}</span></div>
        <div className="inv-stat-card vendido"><span className="inv-stat-label">Vendidos</span><span className="inv-stat-value">{statsCelulares.vendido}</span></div>
      </div>

      <div className="inv-card">
        <div className="inv-toolbar">
          <div className="inv-search-wrapper">
            <svg className="inv-search-icon" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por modelo o color..." className="inv-search-input" />
          </div>
          <button onClick={() => abrirModal()} className="btn-inv-primary">+ Agregar Equipo</button>
        </div>

        <TablaCelulares celulares={celularesFiltrados} onEdit={abrirModal} onDelete={eliminar} onVender={registrarVentaDirecta} />
      </div>

      {modal && (
        <ModalCelular formCelular={formCelular} setFormCelular={setFormCelular} guardar={guardar} setModal={setModal} editId={editId} />
      )}
    </div>
  )
}